// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { Injectable, NgZone, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';

type GoCtor = new () => GoInstance;

interface GoInstance {
  importObject: WebAssembly.Imports;
  argv: string[];
  env: Record<string, string>;
  exited: boolean;
  run(instance: WebAssembly.Instance): Promise<void>;
}

export interface WasmHandle {
  isRunning(): boolean;
  whenExit: Promise<void>;
  getGlobal<T extends Function = Function>(name: string): T | undefined;
  stop(): Promise<void>;
}

@Injectable({ providedIn: 'root' })
export class WasmRuntimeBaseService {
  // ===== Fields =====
  private goCtor: GoCtor | null = null;
  private wasmExecLoading?: Promise<void>;

  // ===== Constructor =====
  constructor(
    private zone: NgZone,
    @Inject(DOCUMENT) private doc: Document,
  ) {}

  // ===== Public API =====
  public async createAndRun(wasmUrl: string): Promise<WasmHandle> {
    await this.ensureWasmExecLoaded();
    if (!this.goCtor) throw new Error('Go constructor not initialized');

    const go = new this.goCtor();

    // Resolve against <base href> so localized builds (e.g. base "/en-US/")
    // fetch from "/en-US/wasm/..." instead of the locale-less "/wasm/...".
    const resolvedUrl = this.resolveAssetUrl(wasmUrl);

    const instantiate = async () => {
      try {
        const url = `${resolvedUrl}?v=${Date.now()}`;
        const resp = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
        if (!resp.ok) throw new Error(`${resp.status} ${resp.statusText}`);
        if ('instantiateStreaming' in WebAssembly) {
          return await (WebAssembly as any).instantiateStreaming(resp, go.importObject);
        }
        const bytes = await resp.arrayBuffer();
        return await WebAssembly.instantiate(bytes, go.importObject as any);
      } catch {
        const bytes = await (await fetch(resolvedUrl, { credentials: 'same-origin' })).arrayBuffer();
        return await WebAssembly.instantiate(bytes, go.importObject as any);
      }
    };

    let running = true;
    let resolveExit!: () => void;
    const whenExit = new Promise<void>((res) => (resolveExit = res));

    const runPromise = this.zone.runOutsideAngular(async () => {
      try {
        const { instance } = await instantiate();
        await go.run(instance); // main with select{} won't return
      } finally {
        running = false;
        resolveExit();
      }
    });

    const getGlobal = <T extends Function = Function>(name: string) =>
      (globalThis as any)[name] as T | undefined;

    const stop = async () => {
      // No explicit disposer on the Go/WASM side; just wait for run to settle if ever.
      try {
        const dispose = getGlobal<() => void>('alignmentViewDispose');
        if (dispose) dispose();
      } catch {}
      try { await runPromise; } catch {}
    };

    return {
      isRunning: () => running,
      whenExit,
      getGlobal,
      stop,
    };
  }

  // ===== Private Helpers =====
  private async ensureWasmExecLoaded(): Promise<void> {
    if (this.goCtor) return;
    if (this.wasmExecLoading) return this.wasmExecLoading;

    this.wasmExecLoading = this.zone.runOutsideAngular(async () => {
      const wasmExecUrl = this.resolveAssetUrl('wasm/wasm_exec.js');
      const present = Array.from(document.scripts).some(s => s.src.includes('/wasm/wasm_exec.js'));
      if (!present) {
        await new Promise<void>((resolve, reject) => {
          const s = document.createElement('script');
          s.src = wasmExecUrl;
          s.async = true;
          s.onload = () => resolve();
          s.onerror = () => reject(new Error(`Failed to load ${wasmExecUrl}`));
          document.head.appendChild(s);
        });
      }
      const ctor = (window as any).Go as GoCtor | undefined;
      if (!ctor) throw new Error('window.Go not found after loading wasm_exec.js');
      this.goCtor = ctor;
      try { delete (window as any).Go; } catch {}
    });

    return this.wasmExecLoading;
  }

  /**
   * Resolve an asset path against the document's <base href>.
   * Localized builds set the base href to "/<locale>/" (e.g. "/en-US/"),
   * so a relative path such as "wasm/foo.wasm" correctly resolves to
   * "/en-US/wasm/foo.wasm" instead of the locale-less "/wasm/foo.wasm".
   * Full URLs and root-absolute "/..." paths are returned as-is by `new URL()`.
   */
  private resolveAssetUrl(path: string): string {
    return new URL(path, this.doc.baseURI).href;
  }
}
